import React, { useState, useEffect, useMemo } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// --- IMPORTS FOR REUSABLE COMPONENTS ---
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// 1. Import Global Settings (UPDATED WITH LEAD TIME CONSTANTS)
import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  QS_DAYS, 
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  QS_TIMELINE_TEXT, 
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  QS_LABEL, QS_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
} from '../config/globalSettings';

// 2. Import Data
import { placardData } from '../data/placardData'; 
import '../App.css';

function PlacardBuilder() {
  // --- STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [placardId, setPlacardId] = useState("");
  const [pistolMagId, setPistolMagId] = useState("");     // Mini Ghidorah S Mag
  const [kaijuId, setKaijuId] = useState("");          
  const [carrierId, setCarrierId] = useState("");     
  const [breakthroughId, setBreakthroughId] = useState("");
  const [rushService, setRushService] = useState(false);

  // --- STABLE REFERENCES FOR VISUALIZER ---
  const gunDataObj = useMemo(() => ({ id: "standard" }), []);
  const lightDataObj = useMemo(() => ({ id: "none" }), []);
  const colorDataObj = useMemo(() => ({ id: placardId }), [placardId]);

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL, generateShareableURL, saveToLocalStorage, loadFromLocalStorage,
    savedConfigs, saveNamedConfig, deleteNamedConfig, copyToClipboard, copySuccess,
    shareModalOpen, setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    placardId, pistolMagId, kaijuId, carrierId, breakthroughId, rushService
  });

  const loadConfig = (config) => {
    if (!config || config.type === 'click') return; 
    setPlacardId(config.placardId || '');
    setPistolMagId(config.pistolMagId || '');
    setKaijuId(config.kaijuId || '');
    setCarrierId(config.carrierId || ''); 
    setBreakthroughId(config.breakthroughId || '');
    setRushService(config.rushService === true);
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (placardId) saveToLocalStorage(getCurrentConfig());
  }, [placardId, pistolMagId, kaijuId, carrierId, breakthroughId, rushService]);

  // --- SELECTIONS ---
  const selectedPlacard = placardData.placards.find(p => p.id === placardId);
  const selectedPistolMag = placardData.pistol_mags?.find(m => m.id === pistolMagId);
  const selectedKaijuDecision = placardData.kaiju_kits?.find(k => k.id === kaijuId); 
  const selectedCarrier = placardData.kaiju_carriers?.find(c => c.id === carrierId); 
  const selectedBreakthrough = placardData.breakthrough.find(b => b.id === breakthroughId);

  // --- CONDITIONAL LOGIC ---
  const showKaijuOption = selectedPlacard?.needs_kaiju === true;
  const showPistolMagOption = selectedPlacard?.needs_pistol_mag === true;
  
  useEffect(() => {
    if (kaijuId !== 'yes') {
        setCarrierId("");
    }
  }, [kaijuId]);

  useEffect(() => {
      if (!showPistolMagOption) {
          setPistolMagId("");
      }
  }, [showPistolMagOption]);

  // --- LEAD TIME LOGIC (UPDATED WITH GLOBALS) ---
  const calculateLeadTime = () => {
    if (!selectedPlacard) return { text: "Select Options", date: "...", type: "PENDING" };

    const targetDays = rushService ? RUSH_DAYS : QS_DAYS;
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (rushService && ENABLE_RUSH_SHIPPING) {
        return { type: "RUSH PRIORITY", timeline: RUSH_TIMELINE_TEXT, date: dateString, color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR), isCustom: true };
    }

    return { type: QS_LABEL, timeline: QS_TIMELINE_TEXT, date: dateString, color: QS_COLOR, bg: leadTimeTint(QS_COLOR), isCustom: false };
  };

  const leadTimeInfo = calculateLeadTime();

  // --- AUTO-CLEAR RUSH WHEN NOT APPLICABLE ---
  useEffect(() => {
    if (rushService && (!leadTimeInfo.isCustom || leadTimeInfo.type === QS_LABEL)) {
        setRushService(false);
    }
  }, [placardId, rushService, leadTimeInfo]);

  // --- PRICING ---
  const subtotal = 
    (selectedPlacard?.price || 0) +
    (showKaijuOption && selectedKaijuDecision ? selectedKaijuDecision.price : 0) + 
    (selectedBreakthrough?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  // Validation
  let isReady = placardId;
  
  if (showPistolMagOption && !pistolMagId) isReady = false;
  
  if (showKaijuOption) {
      if (!kaijuId) isReady = false;
      if (kaijuId === 'yes' && !carrierId) isReady = false;
  }

  // --- ADD TO CART (UPDATED CART TEXT) ---
  const handleAddToCart = () => {
    if (!isReady) return;

    let kaijuDetails = null;
    if (showKaijuOption) {
        if (kaijuId === 'yes') {
            kaijuDetails = `Kaiju Stiffy Kit: Yes\n   - Carrier: ${selectedCarrier?.name}`;
        } else {
            // Declined: leave it off the ticket entirely.
            kaijuDetails = null;
        }
    }

    const configuration = [
        `Model: ${selectedPlacard?.name}`,
        showPistolMagOption ? optionLine("Pistol Mag", selectedPistolMag?.name) : null,
        kaijuDetails,
        optionLine("Breakthrough", selectedBreakthrough?.name),
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`,
    ].filter(Boolean).join('\n');

    const productID = 283905; 
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);

    window.location.href = `/checkout/?${params.toString()}`;
  };

  const shareableURL = generateShareableURL(getCurrentConfig());

  const SectionHeader = ({ title, description, imageUrl }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
        <h3 style={{ textTransform: "uppercase", margin: 0, fontSize: "14px", color: "#D5A678" }}>{title}</h3>
        {(description || imageUrl) && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">
                    {imageUrl && (
                        <div style={{marginBottom: "10px"}}>
                            <img src={imageUrl} alt="Info" style={{width: "100%", borderRadius: "4px", backgroundColor: "transparent"}} />
                        </div>
                    )}
                    {description}
                </div>
            </div>
        )}
    </div>
  );

  return (
    <div className={`builder-container ${placardId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">Placard Builder <br/><span style={{fontSize:"16px", color:"#D5A678"}}>TACTICAL INSERTS & CARRIERS</span></h1>
        
        {/* DESCRIPTION */}
        <div style={{ marginBottom: "20px" }}>
           {selectedPlacard && selectedPlacard.details && (
             <div style={{ backgroundColor: "#222", padding: "15px", borderRadius: "5px", border: "1px solid #444", color: "#ddd", fontSize: "13px", lineHeight: "1.5" }}>
                <div dangerouslySetInnerHTML={{ __html: selectedPlacard.details }} />
             </div>
           )}
        </div>

        {/* PLACARD SELECTION */}
        <OptionSelector 
            label="Select Placard / Insert *" 
            options={placardData.placards} 
            selectedValue={placardId} 
            onChange={(val) => { 
                setPlacardId(val); 
                setKaijuId(""); 
                setCarrierId("");
            }} 
        />

        {/* CONDITIONAL: PISTOL MAG FOR MINI GHIDORAH S */}
        {showPistolMagOption && placardData.pistol_mags && (
            <div className="fade-in-section" style={{marginTop: "15px"}}>
                <OptionSelector 
                    label="Select Pistol Magazine *" 
                    options={placardData.pistol_mags} 
                    selectedValue={pistolMagId} 
                    onChange={setPistolMagId} 
                />
            </div>
        )}
        
        {/* CONDITIONAL: KAIJU STIFFY KIT */}
        {showKaijuOption && placardData.kaiju_kits && (
            <>
                <div style={{marginTop: "15px"}}>
                    <SectionHeader 
                        title="Kaiju Stiffy Kit Option" 
                        description={
                            <div>
                                <strong>Enhanced Stability:</strong> Provides a rigid structure and Velcro mapping to securely attach the Ghidorah insert to various kangaroo pouches.<br/><br/>
                                <strong>Seamless Integration:</strong> The Velcro loop lower section of the Kaiju Stiffy Kit perfectly aligns with the Velcro hook patches on the Ghidorah insert, creating a stable and unified platform.<br/><br/>
                                <strong>Improved Reloading:</strong> The Kaiju Stiffy acts as an extension of the Ghidorah’s flared magwell, guiding magazines for faster and more efficient re-indexing.
                            </div>
                        }
                        imageUrl="/wp-content/uploads/anr-assets/images/info/kaiju_kit.png"
                    />
                    <OptionSelector 
                        label="Add Kaiju Stiffy Kit? *" 
                        options={placardData.kaiju_kits} 
                        selectedValue={kaijuId} 
                        onChange={setKaijuId} 
                    />
                </div>

                {/* CONDITIONAL: SELECT CARRIER */}
                {kaijuId === 'yes' && (
                    <div className="fade-in-section" style={{marginLeft: "15px", borderLeft: "2px solid #D5A678", paddingLeft: "15px", marginTop: "15px"}}>
                        <h4 style={{ color: "#D5A678", marginTop: "0", marginBottom: "10px", fontSize: "14px", textTransform: "uppercase" }}>Select Carrier</h4>
                        <OptionSelector 
                            label="Which carrier will this go in? *" 
                            options={placardData.kaiju_carriers} 
                            selectedValue={carrierId} 
                            onChange={setCarrierId} 
                        />
                    </div>
                )}
            </>
        )}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Add Breakthrough Products</h3>
        <OptionSelector label="Optional firearm maintenance" options={placardData.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* --- COLLAPSIBLE DESCRIPTION --- */}
        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                High-performance KYDEX® Thermoplastics placard insert designed to optimize your magazine carry in plate carriers and chest rigs. This single-unit carrier offers superior retention, ease of use, and a comfortable fit, ensuring your magazines are always ready when you need them.
            </p>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={placardId} />
            </CollapsibleSection>
        </div>

      </div>

      <div className="preview-section">
        <div className="sticky-wrapper">

          <div style={{ aspectRatio: "1 / 1" }}>
              <HolsterVisualizer 
                holsterType="placard" 
                gunData={gunDataObj} 
                lightData={lightDataObj} 
                colorData={colorDataObj} 
                mountData={null} 
                clawData={null}
                isLightbearing="no" 
                viewSide={viewSide} 
              />
          </div>

          {/* --- FRONT / REAR TOGGLE --- */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {/* --- LEAD TIME BOX --- */}
          {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px"
             }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>
                </div>
             </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>${subtotal.toFixed(2)}</span>
                <span style={{ color: "#00ff00" }}>${total}</span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", letterSpacing: "1px" }}>{SALE_NAME} ACTIVE</div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>
          
          {/* DESKTOP DISCLAIMERS */}
          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={placardId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "SELECT OPTIONS"}</button>
        </div>
      </div>
      
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container"><span className="mobile-label">Estimated Total</span><span className="mobile-total">${total}</span></div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "INCOMPLETE"}</button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} shareableURL={shareableURL} currentConfig={getCurrentConfig()} savedConfigs={savedConfigs} onSaveConfig={saveNamedConfig} onLoadConfig={loadConfig} onDeleteConfig={deleteNamedConfig} onCopyLink={copyToClipboard} copySuccess={copySuccess} gunName={selectedPlacard?.name} colorName="Standard" />
    </div>
  );
}

export default PlacardBuilder;